<template>
  <Transition name="route-progress-fade">
    <div
      v-if="isVisible"
      class="route-progress-bar"
      role="progressbar"
      aria-label="Loading"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-valuenow="ariaValueNow"
    >
      <div
        class="route-progress-bar__fill"
        :style="{ transform: `scaleX(${progress})` }"
      />
      <div
        class="route-progress-bar__glow"
        :style="{ left: `${progress * 100}%` }"
      />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useNavigationProgress } from '@/composables/useNavigationProgress'

const { progress, isVisible } = useNavigationProgress()

const ariaValueNow = computed(() => Math.round(progress.value * 100))
</script>

<style scoped>
.route-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  pointer-events: none;
  z-index: 9999;
  background: transparent;
  overflow: hidden;
}

.route-progress-bar__fill {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--theme-accent-primary) 70%, transparent) 0%,
    var(--theme-accent-primary) 60%,
    color-mix(in srgb, var(--theme-accent-primary) 90%, white 10%) 100%
  );
  transform-origin: left center;
  transform: scaleX(0);
  transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.route-progress-bar__glow {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 80px;
  margin-left: -80px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in srgb, var(--theme-accent-primary) 60%, transparent) 100%
  );
  filter: blur(2px);
  transition: left 200ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: left;
}

.route-progress-fade-enter-active,
.route-progress-fade-leave-active {
  transition: opacity 180ms ease;
}

.route-progress-fade-enter-from,
.route-progress-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .route-progress-bar__fill,
  .route-progress-bar__glow {
    transition: none;
  }
}
</style>
